import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Moment } from "moment";
import { Subscription } from "rxjs";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { ContractDetail, ForecastedUnsignedContractsDirectDetailService, UnsignedContractsDirectDetailServiceData } from "../../services/contractsKpi/unsignedContractsDirectDetail.service";
import { ChartDropdownOption } from "../../../../dashboardCommon/components/cardContents/chartDropdown.component";
import { LineChartData, LineChartDataset } from "../../../../dashboardCommon/components/cardContents/lineChart.component";
import { DateColumnDefinition, EmailColumnDefinition, PhoneNumberColumnDefinition, TextColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";
import { ContractsDirectDetailData } from "./contractsDirectDetailData";
import { ImpersonationService } from "src/app/auth/apigee/impersonation.service";

export class UnsignedContractsDirectEvolutionData {
    public lineChartData: LineChartData;
    public dates: Moment[];

    constructor(lineChartData: LineChartData, dates: Moment[]) {
        this.lineChartData = lineChartData;
        this.dates = dates;
    }
}

@Component({
    selector: 'unsigned-contracts-direct-evolution-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class UnsignedContractsDirectEvolutionKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<ForecastedUnsignedContractsDirectDetailService, UnsignedContractsDirectDetailServiceData, ChartDropdownOption[], ContractsDirectDetailData>
    implements OnDestroy {
    public getClassName() { return "UnsignedContractsDirectEvolutionKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryDetailService: ForecastedUnsignedContractsDirectDetailService) {
        super(zone, logger, summaryDetailService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: UnsignedContractsDirectDetailServiceData): ChartDropdownOption[] {
        if (data.contracts) {
            return this.generateOptions(data);
        }
        else return this.getDefaultSummaryData();
    }

    private extractConsultants(data: ContractDetail[]): { userDocNumber: string, name: string }[] {
        let userDocNumbers: { userDocNumber: string, name: string }[] = [];

        if (data) {
            data.forEach(x => {
                if (!userDocNumbers.find(y => x.userDocNumber == y.userDocNumber)) userDocNumbers.push({ userDocNumber: x.userDocNumber, name: x.userName });
            })
        }

        return userDocNumbers;
    }

    private generateOption(optionName: string, data: ContractDetail[], filter: (x: ContractDetail) => boolean): ChartDropdownOption {
        let dateCounter = moment().startOf('day');
        let datasetData: { count: number, label: string, date: Moment }[] = new Array<{ count: number, label: string, date: Moment }>(7);
        datasetData.fill(null, 0, 7);
        datasetData.forEach((x, idx, arr) => {
            arr[idx] = { count: 0, label: dateCounter.date() + " " + this.getMonthName(dateCounter.month()), date: dateCounter }
            dateCounter = dateCounter.clone().add(1, "days");
        });

        data.filter(x => x.type=="C").filter(filter).reduce((acc, x) => {
            let contractDate = moment(x.beginDate);
            let accElement = acc.find((x) => {
                return x.date.date() == contractDate.date() && x.date.month() == contractDate.month();
            });

            if (accElement) accElement.count++;

            return acc;
        }, datasetData);

        let result = new UnsignedContractsDirectEvolutionData(new LineChartData(
            datasetData.map<string>(x => x.label),
            [
                new LineChartDataset("Contratos", datasetData.map<number>(x => x.count))
            ]
        ), datasetData.map<Moment>(x => x.date));

        let option = new ChartDropdownOption();
        option.text = optionName;
        option.value = result;

        return option;
    }

    private generateOptions(data: UnsignedContractsDirectDetailServiceData): ChartDropdownOption[] {
        let consultants = this.extractConsultants(data.contracts);

        let global = this.generateOption("Todos", data.contracts, (x) => true);
        let otherOptions = consultants.map(x => this.generateOption(x.name, data.contracts, y => y.userDocNumber == x.userDocNumber))
            .sort((a, b) => (a.text ? a.text.toLowerCase() : "") < (b.text ? b.text.toLowerCase() : "") ? -1 : 1);

        return [global].concat(otherOptions);
    }

    private getMonthName(month: number): string {
        switch (month) {
            case 0: return "ene";
            case 1: return "feb";
            case 2: return "mar";
            case 3: return "abr";
            case 4: return "may";
            case 5: return "jun";
            case 6: return "jul";
            case 7: return "ago";
            case 8: return "sep";
            case 9: return "oct";
            case 10: return "nov";
            case 11: return "dic";
            default: return "";
        }
    }

    protected getDefaultSummaryData(): ChartDropdownOption[] {
        let dateCounter = moment().startOf('day');
        let datasetData: { count: number, label: string, date: Moment }[] = new Array<{ count: number, label: string, date: Moment }>(7);
        datasetData.fill(null, 0, 7);
        datasetData.forEach((x, idx, arr) => {
            arr[idx] = { count: 0, label: dateCounter.date() + " " + this.getMonthName(dateCounter.month()), date: dateCounter }
            dateCounter = dateCounter.clone().add(1, "days");
        });

        let result = new UnsignedContractsDirectEvolutionData(new LineChartData(
            datasetData.map<string>(x => x.label),
            [
                new LineChartDataset("Contratos", datasetData.map<number>(x => x.count))
            ]
        ), datasetData.map<Moment>(x => x.date));

        let global = new ChartDropdownOption();
        global.text = "Todos";
        global.value = result;

        return [global];
    }

    protected extractUpdateTime(data: UnsignedContractsDirectDetailServiceData): Date {
        return data.updateTimestamp;
    }

    protected processDetailData(data: UnsignedContractsDirectDetailServiceData, args: any): ContractsDirectDetailData {
        if (data.contracts) {
            if (args && args.filter && args.filter.name && args.filter.name != "Todos") {
                let columnDefinitions = [
                    new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY"), true),
                    new TextColumnDefinition("Trabajador"),
                    new PhoneNumberColumnDefinition("Teléfono"),
                    new EmailColumnDefinition("Email"),
                    new TextColumnDefinition("Cliente", null, true),
                    new TextColumnDefinition("Consultor", null, !(args && args.consultant)),
                    new TextColumnDefinition("Unidad", null, true),
                ];

                if (args && args.filter) {
                    if (args.filter.name) columnDefinitions[5].filterTerm = args.filter.name != "Todos" ? [args.filter.name] : null;
                    if (args.filter.date) columnDefinitions[0].filterTerm = args.filter.date;
                }

                let nifConsultor = this.impersonationService.getNifSafe().toUpperCase();
                let tabledata2 = [];
                let tabledata = [];
                for (let contract of data.contracts.filter(x => x.type == "C")) {
                    if (contract.userDocNumber.toUpperCase() == nifConsultor) {
                        tabledata.push([
                            moment(contract.beginDate).startOf('day'),
                            contract.workerName,
                            contract.workerPhone,
                            contract.workerEmail,
                            contract.clientFiscalName,
                            contract.userName,
                            contract.unitName,
                        ]);
                    }
                    else {
                        if (!(args && args.consultant)) {
                            tabledata2.push([
                                moment(contract.beginDate).startOf('day'),
                                contract.workerName,
                                contract.workerPhone,
                                contract.workerEmail,
                                contract.clientFiscalName,
                                contract.userName,
                                contract.unitName,
                            ]);
                        }
                    }
                }
                let arrayOrdenado = tabledata.concat(tabledata2);
                return new ContractsDirectDetailData(columnDefinitions, arrayOrdenado);
            } else {
                let columnDefinitions = [
                    new TextColumnDefinition("Consultor", null, !(args && args.consultant)),
                    new TextColumnDefinition("Unidad", null, true),
                    new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY"), true),
                    new TextColumnDefinition("Trabajador"),
                    new PhoneNumberColumnDefinition("Teléfono"),
                    new EmailColumnDefinition("Email"),
                    new TextColumnDefinition("Cliente", null, true)
                ];

                if (args && args.filter) {
                    if (args.filter.name) columnDefinitions[0].filterTerm = args.filter.name != "Todos" ? [args.filter.name] : null;
                    if (args.filter.date) columnDefinitions[2].filterTerm = args.filter.date;
                }

                let nifConsultor = this.impersonationService.getNifSafe().toUpperCase();
                let tabledata2 = [];
                let tabledata = [];
                for (let contract of data.contracts.filter(x => x.type=="C")) {
                    if (contract.userDocNumber.toUpperCase() == nifConsultor) {
                        tabledata.push([
                            contract.userName,
                            contract.unitName,
                            moment(contract.beginDate).startOf('day'),
                            contract.workerName,
                            contract.workerPhone,
                            contract.workerEmail,
                            contract.clientFiscalName,
                        ]);
                    }
                    else {
                        if (!(args && args.consultant)) {
                            tabledata2.push([
                                contract.userName,
                                contract.unitName,
                                moment(contract.beginDate).startOf('day'),
                                contract.workerName,
                                contract.workerPhone,
                                contract.workerEmail,
                                contract.clientFiscalName,
                            ]);
                        }
                    }
                }
                let arrayOrdenado = tabledata.concat(tabledata2);
                return new ContractsDirectDetailData(columnDefinitions, arrayOrdenado);
            }
        }
        else return this.getDefaultDetailData();
    }

    protected getDefaultDetailData(): ContractsDirectDetailData {
        let columnDefinitions = [
            new TextColumnDefinition("Consultor", null, true),
            new TextColumnDefinition("Unidad", null, true),
            new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY"), true),
            new TextColumnDefinition("Trabajador"),
            new PhoneNumberColumnDefinition("Teléfono"),
            new EmailColumnDefinition("Email"),
            new TextColumnDefinition("Cliente", null, true)
        ];

        return new ContractsDirectDetailData(columnDefinitions, []);
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }

    public mergeFilter(filter: any) {
        let detailProcessingArgs = this.detailProcessingArgs || {};
        detailProcessingArgs.filter = detailProcessingArgs.filter || {};
        detailProcessingArgs.filter = { ...detailProcessingArgs.filter, ...filter };
        this.detailProcessingArgs = detailProcessingArgs;
    }

    public showDetailEx(event: Event, prefilter: any) {
        this.mergeFilter(prefilter);
        this.showDetail(event);
    }
}
